import React, { Suspense } from 'react';

import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import moment from 'moment';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { Provider } from 'react-redux';

import { Equipment } from '@pages/Equipment';
import { startDynamicReactRootsObserver } from '@shared/lib';

import store from './store';

import './app/styles/index.css';

i18next.use(initReactI18next);

moment.locale(LOCALE);

Sentry.init({
    environment: process.env.SENTRY_ENVIRONMENT,
    dsn: process.env.SENTRY_DSN_FRONT,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

const renderReactComponent = async (
    selector,
    loader,
    { props = {}, suspenseFallback = 'Загрузка...', exportName = 'default', onError } = {}
) => {
    const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!rootElement) return;
    if (
        rootElement.dataset.reactComponentMounted === 'true' ||
        rootElement.dataset.reactComponentMounting === 'true'
    ) {
        return;
    }

    rootElement.dataset.reactComponentMounting = 'true';

    try {
        await window.i18nextInitPromise;
        const module = await loader();
        const Component = module[exportName];

        if (!Component) {
            if (onError) {
                throw new Error(`Компонент с именем "${exportName}" не найден`);
            }

            console.error(`Компонент с именем "${exportName}" не найден`);
            return;
        }

        const queryClient = new QueryClient();
        const root = createRoot(rootElement);
        const componentTree = (
            <Suspense fallback={<div>{suspenseFallback}</div>}>
                <QueryClientProvider client={queryClient}>
                    <I18nextProvider i18n={i18next}>
                        <Provider store={store}>
                            <Component {...props} />
                        </Provider>
                    </I18nextProvider>
                </QueryClientProvider>
            </Suspense>
        );
        root.render(
            onError ? (
                <ErrorBoundary fallback={null} onError={onError}>
                    {componentTree}
                </ErrorBoundary>
            ) : (
                componentTree
            )
        );
        rootElement.dataset.reactComponentMounted = 'true';
    } catch (error) {
        onError?.(error);
        throw error;
    } finally {
        delete rootElement.dataset.reactComponentMounting;
    }
};

const renderBoard = (selector) =>
    renderReactComponent(selector, () => import('./components/Board/Board'), {
        exportName: 'Board',
    });
const renderPaymentsList = (selector) =>
    renderReactComponent(selector, () => import('@widgets/PaymentsList/ui/PaymentsList'), {
        exportName: 'PaymentsList',
    });

const renderApplicationsPayment = (selector) =>
    renderReactComponent(
        selector,
        () => import('./TemporaryConmonents/ApplicationsPayment/ApplicationsPayment'),
        {
            exportName: 'ApplicationsPayment',
        }
    );
const renderAccounts = (selector) =>
    renderReactComponent(selector, () => import('./TemporaryConmonents/Accounts/Accounts'), {
        exportName: 'Accounts',
    });

const renderMassActionsPanel = (selector) =>
    renderReactComponent(selector, () => import('./components/MassActionsPanel/MassActionsPanel'), {
        exportName: 'MassActionsPanel',
    });

const renderAddPayments = (selector) =>
    renderReactComponent(selector, () => import('@widgets'), {
        exportName: 'AddPayments',
    });
const renderFilterPayments = (selector) =>
    renderReactComponent(selector, () => import('@widgets'), {
        exportName: 'FilterPayments',
    });

const renderRegister = (selector) =>
    renderReactComponent(
        selector,
        () => import('./TemporaryConmonents/Authorization/Register/Register'),
        {
            exportName: 'Register',
        }
    );

const renderFilterSub = (selector) =>
    renderReactComponent(selector, () => import('./TemporaryConmonents/FilterSub/FilterSub'), {
        exportName: 'FilterSub',
    });

const renderReset = (selector) =>
    renderReactComponent(
        selector,
        () => import('./TemporaryConmonents/Authorization/Reset/Reset'),
        {
            exportName: 'Reset',
        }
    );

const renderLogin = (selector) =>
    renderReactComponent(
        selector,
        () => import('./TemporaryConmonents/Authorization/Login/Login'),
        {
            exportName: 'Login',
        }
    );

const renderForgotPassword = (selector) =>
    renderReactComponent(
        selector,
        () => import('./TemporaryConmonents/Authorization/ForgotPassword/ForgotPassword'),
        {
            exportName: 'ForgotPassword',
        }
    );

const renderErrorPage = (selector) =>
    renderReactComponent(selector, () => import('./TemporaryConmonents/ErrorPage/ErrorPage'), {
        exportName: 'ErrorPage',
    });

const renderGant = (selector) =>
    renderReactComponent(selector, () => import('./TemporaryConmonents/Gant/ReactGant'), {
        useStore: true,
        exportName: 'ReactGant',
    });

const renderDashboard = (selector) =>
    renderReactComponent(
        selector,
        () =>
            import('./pages/Dashboard/Dashboard').then((component) => ({
                Dashboard: (props) => (
                    <NuqsAdapter>
                        <component.Dashboard {...props} />
                    </NuqsAdapter>
                ),
            })),
        {
            exportName: 'Dashboard',
        }
    );

const renderCrm = (selector) => {
    const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;
    const dashboardRoutes = rootElement?.dataset?.crmDashboardRoutes
        ? JSON.parse(rootElement.dataset.crmDashboardRoutes)
        : {};

    if (rootElement) {
        // CRM живет как fullscreen app внутри legacy Twig-страницы.
        // contain изолирует overflow legacy Dashboard и не дает ему раздувать scroll у html.
        Object.assign(rootElement.style, {
            height: '100vh',
            overflow: 'hidden',
            contain: 'layout paint',
        });
    }

    return renderReactComponent(selector, () => import('@crm'), {
        exportName: 'CrmApp',
        props: {
            basename: rootElement?.dataset?.crmBasename || '/crm',
            companyId: rootElement?.dataset?.crmCompanyId,
            dashboardRoutes,
            tariffPaymentUrl: rootElement?.dataset?.crmTariffPaymentUrl,
        },
    });
};

const parseRequiredBooleanDataAttribute = (value, attributeName) => {
    if (value === 'true') return true;
    if (value === 'false') return false;

    throw new TypeError(`Атрибут ${attributeName} должен иметь значение "true" или "false"`);
};

const validateTariffModalData = ({ canUseCrmByTariff, tariffModalUrl, tariffModalContent }) => {
    const modalPayloadCount = [tariffModalUrl, tariffModalContent].filter(Boolean).length;

    if (!canUseCrmByTariff && modalPayloadCount !== 1) {
        throw new TypeError(
            'При тарифном ограничении должен быть передан ровно один атрибут модалки'
        );
    }

    if (canUseCrmByTariff && modalPayloadCount !== 0) {
        throw new TypeError('Атрибуты тарифной модалки не должны передаваться при доступной CRM');
    }
};

const renderSmetterModuleSwitcher = (selector) => {
    const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;
    const source = rootElement?.dataset?.source;
    const hideConstructionSkeleton = () =>
        rootElement?.classList.remove('construction-module-switcher-root');

    if (source !== 'crm' && source !== 'construction') {
        hideConstructionSkeleton();
        console.error(`Неизвестный источник переключателя модулей: "${source}"`);
        return undefined;
    }

    let props = { source };

    if (source === 'construction') {
        try {
            const constructionProps = {
                source,
                canUseCrmByTariff: parseRequiredBooleanDataAttribute(
                    rootElement.dataset.canUseCrmByTariff,
                    'data-can-use-crm-by-tariff'
                ),
                canUseCrmByEmployee: parseRequiredBooleanDataAttribute(
                    rootElement.dataset.canUseCrmByEmployee,
                    'data-can-use-crm-by-employee'
                ),
                tariffModalUrl: rootElement.dataset.tariffModalUrl,
                tariffModalContent: rootElement.dataset.tariffModalContent,
            };

            validateTariffModalData(constructionProps);
            props = constructionProps;
        } catch (error) {
            hideConstructionSkeleton();
            console.error('Некорректный контракт переключателя модулей', error);
            return undefined;
        }
    }

    return renderReactComponent(selector, () => import('@crm/features/smetter-module-switcher'), {
        exportName: 'SmetterModuleSwitcher',
        props,
        onError: hideConstructionSkeleton,
    });
};

const renderEstimateMeasurements = (selector) =>
    renderReactComponent(
        selector,
        () => import('./TemporaryConmonents/EstimateMeasurements/EstimateMeasurements'),
        {
            exportName: 'EstimateMeasurements',
        }
    );

const renderProjectTeam = (selector) =>
    renderReactComponent(selector, () => import('./TemporaryConmonents/AddProject/AddProject'), {
        exportName: 'AddProject',
    });

export const renderNotification = (selector) =>
    renderReactComponent(
        selector,
        () => import('./TemporaryConmonents/Notification/Notification'),
        {
            exportName: 'Notification',
        }
    );

const renderTasksBoard = (selector) =>
    renderReactComponent(selector, () => import('./pages/TaskBoard'), {
        exportName: 'TaskBoardPage',
    });

const renderMyTeam = (selector) =>
    renderReactComponent(selector, () => import('./widgets/MyTeam'), {
        exportName: 'MyTeam',
    });

const renderAddEmployeeFromCompanyPage = (selector) =>
    renderReactComponent(selector, () => import('./widgets/AddEmployeeFromCompanyPage'), {
        exportName: 'AddEmployeeFromCompanyPage',
    });
const renderGenerateEstimate = (selector) => {
    const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;

    return renderReactComponent(selector, () => import('./widgets/GenerateEstimate'), {
        exportName: 'GenerateEstimate',
        props: {
            widgetId: rootElement?.dataset?.widgetId,
        },
    });
};
const renderPurchaseChart = (selector) =>
    renderReactComponent(selector, () => import('./widgets/PurchaseChart'), {
        exportName: 'PurchaseChart',
    });
const renderEstimateInformation = (selector) =>
    renderReactComponent(selector, () => import('./widgets'), {
        exportName: 'EstimateInformation',
    });
const renderWidgetsShop = (selector) => {
    const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;

    return renderReactComponent(selector, () => import('./widgets'), {
        exportName: 'WidgetsShop',
        props: {
            activeCompanyTariff: rootElement?.dataset?.activeCompanyTariff || '',
        },
    });
};

const renderWidgetShopBanner = (selector) =>
    renderReactComponent(selector, () => import('./widgets/WidgetsShop/ui/WidgetShopBanner'), {
        exportName: 'WidgetShopBanner',
    });
const renderUserPoll = (selector) =>
    renderReactComponent(selector, () => import('./widgets'), {
        exportName: 'UserPoll',
    });
const renderPositionChecklist = (selector) => {
    const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;

    return renderReactComponent(selector, () => import('./features'), {
        exportName: 'Checklist',
        props: {
            positionGuid: rootElement?.getAttribute('data-position-guid-for-checklist'),
        },
    });
};
// const renderComments = (selector) => {
//     const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;
//
//     return renderReactComponent(selector, () => import('./widgets'), {
//         exportName: 'Comments',
//         props: {
//             positionGuid: rootElement?.getAttribute('data-position-guid-for-checklist'),
//         },
//     });
// };
const renderCommentsModalHost = (selector) =>
    renderReactComponent(selector, () => import('./widgets'), {
        exportName: 'CommentsModalHost',
    });
const renderCommentsPollingHost = (selector) =>
    renderReactComponent(selector, () => import('./widgets'), {
        exportName: 'CommentsPollingHost',
    });
const renderAdminConversation = (selector) =>
    renderReactComponent(selector, () => import('./pages'), {
        exportName: 'AdminConversation',
    });
const renderAdminConversationSendForm = (selector) =>
    renderReactComponent(
        selector,
        () =>
            import('./pages').then((component) => ({
                AdminConversationSendForm: () => (
                    <NuqsAdapter>
                        <component.AdminConversationSendForm />
                    </NuqsAdapter>
                ),
            })),
        { exportName: 'AdminConversationSendForm' }
    );
const renderConversationBanner = (selector) =>
    renderReactComponent(
        selector,
        () => import('./widgets/ConversationBanner/ui/ConversationBanner'),
        {
            exportName: 'ConversationBanner',
        }
    );

const renderOrdersRecognition = (selector) =>
    renderReactComponent(
        selector,
        () =>
            import('./widgets').then((component) => ({
                OrdersRecognition: (props) => (
                    <NuqsAdapter>
                        <component.OrdersRecognition {...props} />
                    </NuqsAdapter>
                ),
            })),
        { exportName: 'OrdersRecognition' }
    );

const renderOrdersRecognitionHost = (selector) =>
    renderReactComponent(
        selector,
        () =>
            import('./app/hosts/OrdersRecognitionHost').then((component) => ({
                OrdersRecognitionHost: (props) => (
                    <NuqsAdapter>
                        <component.OrdersRecognitionHost {...props} />
                    </NuqsAdapter>
                ),
            })),
        { exportName: 'OrdersRecognitionHost' }
    );

const renderWidgetActionHost = (selector) => {
    const rootElement = typeof selector === 'string' ? document.querySelector(selector) : selector;

    return renderReactComponent(
        selector,
        () =>
            import('./app/hosts/WidgetActionHost').then((component) => ({
                WidgetActionHost: (props) => <component.WidgetActionHost {...props} />,
            })),
        {
            exportName: 'WidgetActionHost',
            props: {
                elementIdentifier: rootElement?.dataset?.widgetActionIdentifier,
                triggerSelector: rootElement?.dataset?.widgetActionTriggerSelector,
            },
        }
    );
};

const renderAddRequestMaterial = (selector) =>
    renderReactComponent(
        selector,
        () =>
            import('./features').then((component) => ({
                AddRequestMaterial: (props) => (
                    <NuqsAdapter>
                        <component.AddRequestMaterial {...props} />
                    </NuqsAdapter>
                ),
            })),
        { exportName: 'AddRequestMaterial' }
    );

const renderEquipment = (selector) =>
    renderReactComponent(
        selector,
        () =>
            import('./pages').then((component) => ({
                Equipment: (props) => (
                    <NuqsAdapter>
                        <component.Equipment {...props} />
                    </NuqsAdapter>
                ),
            })),
        { exportName: 'Equipment' }
    );

const renderFinanceExport = (selector) =>
    renderReactComponent(selector, () => import('./pages'), {
        exportName: 'FinanceExport',
    });

const renderFinanceDashboard = (selector, propsArg) => {
    const element = typeof selector === 'string' ? document.querySelector(selector) : selector;
    const attrProps = element ? JSON.parse(element.getAttribute('data-react-props') || '{}') : {};

    return renderReactComponent(selector, () => import('./pages'), {
        exportName: 'FinanceDashboard',
        props: { ...attrProps, ...propsArg },
        suspenseFallback: '',
    });
};

const renderFinanceOverview = (selector, propsArg) => {
    const element = typeof selector === 'string' ? document.querySelector(selector) : selector;
    const attrProps = element ? JSON.parse(element.getAttribute('data-react-props') || '{}') : {};

    return renderReactComponent(selector, () => import('./pages'), {
        exportName: 'FinanceOverview',
        props: { ...attrProps, ...propsArg },
        suspenseFallback: '',
    });
};

const renderTeamTrialBanner = (selector) =>
    renderReactComponent(selector, () => import('./widgets'), {
        exportName: 'TeamTrialBanner',
    });
const renderReceiptImage = (selector, propsArg) => {
    const element = typeof selector === 'string' ? document.querySelector(selector) : selector;
    const attrProps = element ? JSON.parse(element.getAttribute('data-react-props') || '{}') : {};
    return renderReactComponent(selector, () => import('./features'), {
        exportName: 'ReceiptImage',
        props: { ...attrProps, ...propsArg },
    });
};

const renderHints = (selector) =>
    renderReactComponent(
        selector,
        () => import('./features/hints/ui/HintsOrchestrator/HintsOrchestrator'),
        {
            exportName: 'HintsOrchestrator',
        }
    );

const renderKnowledgeAssistant = (selector) =>
    renderReactComponent(selector, () => import('./widgets/KnowledgeAssistant'), {
        exportName: 'KnowledgeAssistant',
    });

const reactRoots = {
    renderBoard,
    renderReset,
    renderRegister,
    renderLogin,
    renderForgotPassword,
    renderErrorPage,
    renderAccounts,
    renderFilterSub,
    renderAddPayments,
    renderPaymentsList,
    renderApplicationsPayment,
    renderFilterPayments,
    renderGant,
    renderMassActionsPanel,
    renderEstimateMeasurements,
    renderDashboard,
    renderCrm,
    renderSmetterModuleSwitcher,
    renderProjectTeam,
    renderNotification,
    renderTasksBoard,
    renderMyTeam,
    renderAddEmployeeFromCompanyPage,
    renderGenerateEstimate,
    renderWidgetsShop,
    renderWidgetShopBanner,
    renderEstimateInformation,
    renderPurchaseChart,
    renderUserPoll,
    renderOrdersRecognition,
    renderOrdersRecognitionHost,
    renderWidgetActionHost,
    renderAdminConversation,
    renderAdminConversationSendForm,
    renderConversationBanner,
    renderTeamTrialBanner,
    renderFinanceExport,
    renderFinanceOverview,
    renderFinanceDashboard,
    renderPositionChecklist,
    renderHints,
    renderKnowledgeAssistant,
    // renderComments,
    renderCommentsModalHost,
    renderCommentsPollingHost,
    renderAddRequestMaterial,
    renderReceiptImage,
    renderEquipment,
};

window.reactRoots = reactRoots;

startDynamicReactRootsObserver({ rootsMap: reactRoots });
